﻿import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import hexToRgba from 'hex-to-rgba';

import definitions from '../Style/LfStyleDefinitions';
import {iconCode, mediaQuery, clearFix} from '../Style/cssHelpers';

export default class StepProgressBar extends React.Component {
    constructor(props) {
        super(props);
    }

    static get propTypes() {
        return {
            stepLabels: PropTypes.array.isRequired,
            activeStep: PropTypes.number.isRequired,
            textColor: PropTypes.string,
            noHeading: PropTypes.bool,
            responsiveHideStepOne: PropTypes.bool,
            fadeOutOnLastStep: PropTypes.bool,
            hideDescOnResp: PropTypes.bool,
            edgeMargin: PropTypes.bool,
            noMobilePadding: PropTypes.bool,
            clickableSteps: PropTypes.bool,
            onStepClick: PropTypes.func
        }
    }

    render() {
        const steps = this.props.stepLabels;
        const noOfSteps = this.props.stepCount;
        const activeStep = this.props.activeStep || 0;
        const hideOnResponsive = typeof this.props.hideDescOnResp !== 'undefined'
            ? this.props.hideDescOnResp
            : false;
        const edgeMargin = typeof this.props.edgeMargin !== 'undefined'
            ? this.props.edgeMargin
            : false;
        const clickableSteps = typeof this.props.clickableSteps !== 'undefined'
            ? this.props.clickableSteps
            : false;
        const onStepClick = this.props.onStepClick;

        return (
            <BarWrapper
                noMobilePadding={this.props.noMobilePadding}
                responsiveHideStepOne={this.props.responsiveHideStepOne}
                fadeOutOnLastStep={this.props.fadeOutOnLastStep}
                noOfSteps={noOfSteps}
                activeStep={activeStep}
            >
                {[...Array(noOfSteps)].map((_, step) => (
                    <SectionStatus
                        key={step}
                        noOfSteps={noOfSteps}
                        textColor={this.props.textColor}
                        completed={activeStep > step ? true : false}
                        active={activeStep == step ? true : false}
                        edgeMargin={edgeMargin}
                        clickableSteps={clickableSteps && step < activeStep}
                        onClick={clickableSteps && step < activeStep
                            ? () => onStepClick(step)
                            : undefined
                        }
                    >
                        <Step>Steg {parseInt(step) + 1}</Step>

                        {steps.length > 0 &&
                            <StepDescription hideOnResponsive={hideOnResponsive}>
                                {steps[step]}
                            </StepDescription>
                        }

                    </SectionStatus>
                ))}
            </BarWrapper>
        );
    }
}


// exclude props from being passed to the DOM
const barPropsToExclude = ['responsiveHideStepOne', 'fadeOutOnLastStep', 'activeStep', 'noOfSteps', 'noMobilePadding'];


const BarWrapper = styled.div.withConfig({
    shouldForwardProp: (prop) => !barPropsToExclude.includes(prop)
})`
    position: relative;
    width: 100%;

    ${mediaQuery.custom(`
        padding: 0px 17px;
    `, '1234px')}

    ${props => props.noMobilePadding ? `
        ${mediaQuery.xsmall(`
            padding: 0px;
        `)}
    ` : `` }
    

    transition: ${props => props.fadeOutOnLastStep ? 'max-height 350ms ease-out, opacity 350ms ease-out' : 'none'};

    ${props => props.fadeOutOnLastStep && props.activeStep == props.noOfSteps ? `
        opacity: 0;
        overflow: hidden;
        max-height: 0;
    ` : `
        opacity: 1;
        overflow: auto;
        max-height: 300px;
    `}

    ${props => mediaQuery.small(`
        transition: ${props.responsiveHideStepOne ? 'max-height 350ms ease-out, opacity 350ms ease-out' : 'none'};

        ${props.responsiveHideStepOne && props.activeStep == 0 ? `
            opacity: 0;
            overflow: hidden;
            max-height: 0;
        ` : `
            opacity: 1;
            overflow: auto;
            max-height: 300px;
        `}
    `)}
`;

const sectionMargin = 26;
const responsiveRowCount = 2;

// exclude props from being passed to the DOM
const sectionPropsToExclude = ['noOfSteps', 'completed', 'edgeMargin', 'active', 'textColor', 'clickableSteps'];

const SectionStatus = styled.div.withConfig({
    shouldForwardProp: (prop) => !sectionPropsToExclude.includes(prop)
})`
    position: relative;
    display: inline-block;

    padding: 20px 20px 20px ${props => props.completed ? '20px' : '0'};

    ${props => props.clickableSteps &&
        'cursor: pointer;'
    }

    ${props => props.edgeMargin ? `
        width: calc(${(100/(props.noOfSteps + 0.01))}% - ${sectionMargin}px);
        margin-left: ${sectionMargin/2}px;
        margin-right: ${sectionMargin/2}px;
    ` : `
        width: calc(${(100/(props.noOfSteps + 0.01))}% - ${sectionMargin}px + ${sectionMargin/props.noOfSteps}px);
        &:not(:first-of-type) {
            margin-left: ${sectionMargin/2}px;
        }

        &:not(:last-of-type) {
            margin-right: ${sectionMargin/2}px;
        }
    `};

    // two rows

    ${props => props.noOfSteps > 3 ? mediaQuery.custom(`
        width: calc(${(100/(parseInt(props.noOfSteps + 0.01))) * responsiveRowCount}% - ${sectionMargin/2}px);
        padding: 8px 10px 8px 8px;
        margin: ${sectionMargin/4}px ${sectionMargin/4}px 0 ${sectionMargin/4}px;

        &:first-of-type {
            margin-top: 25px;
        }
    `, '550px') : ''};

    ${props => props.noOfSteps > 4 ? mediaQuery.custom(`
        width: calc(${(100/(parseInt(props.noOfSteps + 0.01))) * responsiveRowCount}% - ${sectionMargin/2}px);
        padding: 8px 10px 8px 8px;
        margin: ${sectionMargin/4}px ${sectionMargin/4}px 0 ${sectionMargin/4}px;

        &:first-of-type {
            margin-top: 25px;
        }
    `, '1200px') : ''};

    border-bottom: 4px solid ${props => props.active ? definitions.colors.green : ( !props.active && !props.completed ? definitions.colors.grayBorder : 'transparent' ) };
    background-color: ${props => props.completed ? hexToRgba(definitions.colors.green, 0.5) : 'transparent'};

    transition: all 300ms ease-out;

    font-family: ${definitions.fonts.heading};
    font-size: 18px;
    font-weight: bold;
    color: ${props => props.textColor ? props.textColor : 'black'};
    text-align: left;

    ${mediaQuery.medium(`
        font-size: 18px;
    `)};

    ${mediaQuery.small(`
        font-size: 16px;
        padding: 8px 10px 8px 0px;
    `)};

    ${mediaQuery.custom(`
        font-size: 12px;
    `, '362px')};

    ${props => props.completed ? `
        color: white;
        ${mediaQuery.small(`
            font-size: 16px;
            padding: 8px 10px 8px 8px;
        `)};
    ` : ''};

    ${props => props.completed ? `
        &:after {
            position: absolute;

            ${iconCode.checkCircle()};
            right: 10%;
            top: 50%;
            transform: translateY(-50%);

            color: white;

            ${mediaQuery.xxsmall(`
                right: 5px;
                font-size: 12px;
            `)}

            ${mediaQuery.custom(`
                display: none;
            `, '420px')}

            ${mediaQuery.small(`
                right: 15px;
                font-size: 14px;
            `)}

            ${mediaQuery.medium(`
                right: 10px;
            `)}
        }
    ` : ''};

`;

const Step = styled.span`
    display: block;
    width: 100%;

    font-family: ${definitions.fonts.heading};
    font-size: 18px;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;

    ${mediaQuery.small(`
        font-size: 14px;
    `)}
`;

// exclude props from being passed to the DOM
const stepPropsToExclude = ['hideOnResponsive'];

const StepDescription = styled.span.withConfig({
    shouldForwardProp: (prop) => !stepPropsToExclude.includes(prop)
})`
    font-family: ${definitions.fonts.main};

    display:inline-block;
    ${props => props.hideOnResponsive ? `
        ${mediaQuery.custom(`
            display: none;
        `, '810px')}
    ` : ''}
`;
